<template>
  <div
    class="rounded-xl border border-gray-100 bg-white p-4 shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
    :class="statusClasses.card"
  >
    <div class="flex items-start justify-between">
      <div class="space-y-2">
        <p class="flex items-center gap-2 text-sm font-semibold text-cyan-700">
          <span>{{ titulo }}</span>
          <span
            v-if="descripcion"
            class="inline-flex h-5 w-5 items-center justify-center rounded-full border border-cyan-200 bg-cyan-50 text-[10px] font-bold text-cyan-700"
            :title="descripcion"
            aria-label="Mas informacion"
          >
            i
          </span>
        </p>
        <p class="text-3xl font-bold text-gray-900">{{ formattedValue }}</p>
        <p class="text-xs font-medium text-gray-500">Meta: {{ formattedMeta }}</p>
      </div>
      <span
        class="rounded-full px-2.5 py-1 text-xs font-semibold"
        :class="statusClasses.badge"
      >
        {{ statusLabel }}
      </span>
    </div>
    <p v-if="diffLabel" class="mt-3 text-xs font-medium text-gray-600">
      {{ diffLabel }}
    </p>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'KpiCard',
  props: {
    titulo: { type: String, required: true },
    valorReal: { type: [Number, String], default: null },
    meta: { type: [Number, String], default: null },
    descripcion: { type: String, default: '' },
    tipo: {
      type: String,
      default: 'percent', // percent | currency | number
    },
    direccion: {
      type: String,
      default: 'higher', // higher | lower
      validator: (val) => ['higher', 'lower'].includes(val),
    },
  },
  setup(props) {
    const currencyFormatter = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    });

    const toNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const normalizeValue = (value, type) => {
      const numeric = toNumber(value);
      if (numeric === null) return null;
      if (type === 'percent') {
        return numeric <= 1 ? numeric * 100 : numeric;
      }
      return numeric;
    };

    const realValue = computed(() => normalizeValue(props.valorReal, props.tipo));
    const metaValue = computed(() => normalizeValue(props.meta, props.tipo));

    const formatValue = (value) => {
      if (value === null || value === undefined || Number.isNaN(value)) return '--';
      if (props.tipo === 'currency') return currencyFormatter.format(Number(value));
      if (props.tipo === 'percent') return `${Number(value).toFixed(1)} %`;
      return Number(value).toLocaleString('es-AR');
    };

    const tolerance = computed(() => {
      const meta = metaValue.value;
      if (meta === null) return 0;
      const base = Math.abs(meta);
      return Math.max(base * 0.1, 1);
    });

    const status = computed(() => {
      if (realValue.value === null || metaValue.value === null) return 'neutral';
      const diff = realValue.value - metaValue.value;
      const tol = tolerance.value;
      if (props.direccion === 'higher') {
        if (realValue.value >= metaValue.value) return 'good';
        if (realValue.value >= metaValue.value - tol) return 'warn';
        return 'bad';
      }
      // lower is better
      if (realValue.value <= metaValue.value) return 'good';
      if (realValue.value <= metaValue.value + tol) return 'warn';
      return 'bad';
    });

    const diffLabel = computed(() => {
      if (realValue.value === null || metaValue.value === null) return '';
      const delta = realValue.value - metaValue.value;
      const sign = delta > 0 ? '+' : '';
      if (props.tipo === 'percent') {
        return `Delta vs meta: ${sign}${delta.toFixed(1)} pts`;
      }
      if (props.tipo === 'currency') {
        return `Delta vs meta: ${sign}${currencyFormatter.format(delta)}`;
      }
      return `Delta vs meta: ${sign}${delta.toFixed(1)}`;
    });

    const statusLabel = computed(() => {
      if (status.value === 'good') return 'En meta';
      if (status.value === 'warn') return 'Cerca (-10%)';
      if (status.value === 'bad') return 'Fuera de meta';
      return 'Sin meta';
    });

    const statusClasses = computed(() => {
      const map = {
        good: {
          card: 'border-green-100',
          badge: 'bg-green-100 text-green-800 border border-green-200',
        },
        warn: {
          card: 'border-amber-100',
          badge: 'bg-amber-100 text-amber-800 border border-amber-200',
        },
        bad: {
          card: 'border-rose-100',
          badge: 'bg-rose-100 text-rose-800 border border-rose-200',
        },
        neutral: {
          card: 'border-gray-100',
          badge: 'bg-gray-100 text-gray-700 border border-gray-200',
        },
      };
      return map[status.value] || map.neutral;
    });

    const formattedValue = computed(() => formatValue(realValue.value));
    const formattedMeta = computed(() => formatValue(metaValue.value));

    return {
      formattedValue,
      formattedMeta,
      statusClasses,
      statusLabel,
      diffLabel,
    };
  },
};
</script>
